import React, { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Typography, Divider, Grid, TextField, Button, Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Avatar, Chip, Stack, Card, CardContent } from '@mui/material';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { useAuth } from '../contexts/AuthContext';
import { workerScheduleService } from '../services/workerScheduleService';
import { workerService } from '../services/workerService';
import { companyService } from '../services/companyService';
import { WorkerSchedule } from '../types';

dayjs.extend(localizedFormat);

const DAYS: Array<{ key: string; label: string }> = [
  { key: 'Monday', label: 'Lunes' },
  { key: 'Tuesday', label: 'Martes' },
  { key: 'Wednesday', label: 'Miércoles' },
  { key: 'Thursday', label: 'Jueves' },
  { key: 'Friday', label: 'Viernes' },
  { key: 'Saturday', label: 'Sábado' },
  { key: 'Sunday', label: 'Domingo' },
];

const formatTime12h = (t?: string | null) => {
  if (!t) return '-';
  const parsed = dayjs(t, ['HH:mm:ss', 'HH:mm']);
  return parsed.isValid() ? parsed.format('h:mm A') : t;
};

export const WorkerDashboardPage: React.FC = () => {
  const { user, logout } = useAuth();
  const [schedules, setSchedules] = useState<WorkerSchedule[]>([]);
  const [companyName, setCompanyName] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [form, setForm] = useState({ new_worker_id: '', new_pin: '' });

  const schedulesByDay = useMemo(() => {
    const map: Record<string, WorkerSchedule | undefined> = {};
    schedules.forEach(s => { map[s.day_of_week] = s; });
    return map;
  }, [schedules]);

  useEffect(() => {
    const load = async () => {
      try {
        setError(null);
        const [mySchedules] = await Promise.all([
          workerScheduleService.getMySchedules(),
        ]);
        setSchedules(mySchedules);
      } catch (e: any) {
        setError(e?.message || 'Error cargando datos');
      }
    };
    load();
  }, []);

  useEffect(() => {
    // company name if present in user context
    // optional; Workers endpoint already includes company_name in other views
    setCompanyName((user as any)?.company_name || '');
  }, [user]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await workerService.updateMyCredentials({
        new_worker_id: form.new_worker_id || undefined,
        new_pin: form.new_pin || undefined,
      });
      setSuccess('Datos actualizados');
      setForm({ new_worker_id: '', new_pin: '' });
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudieron actualizar los datos');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, background: '#f7f9fc', minHeight: '100vh' }}>
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2, background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)', color: 'white' }}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
            <Avatar sx={{ bgcolor: 'white', color: '#1976d2', width: 56, height: 56 }}>
              {(user?.name || 'T')[0]?.toUpperCase?.()}
            </Avatar>
          </Grid>
          <Grid item xs>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>Hola, {user?.name}</Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              <Chip size="small" label="Trabajador" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }} />
              {companyName && <Chip size="small" label={companyName} sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }} />}
            </Stack>
          </Grid>
          <Grid item>
            <Button variant="outlined" color="inherit" onClick={() => { logout(); window.location.href = '/login'; }}>
              Cerrar sesión
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Mi Información</Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={1.2}>
                <Typography><b>Nombre:</b> {user?.name}</Typography>
                {companyName && <Typography><b>Empresa:</b> {companyName}</Typography>}
                <Typography sx={{ color: 'text.secondary' }}>Último acceso: {dayjs().format('DD MMM YYYY, h:mm A')}</Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Actualizar mis datos</Typography>
              <Divider sx={{ mb: 2 }} />
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
              <Box component="form" onSubmit={handleSave}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Nuevo ID de trabajador"
                      placeholder="Ej: 123456"
                      fullWidth
                      value={form.new_worker_id}
                      onChange={e => setForm({ ...form, new_worker_id: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Nuevo PIN"
                      type="password"
                      placeholder="******"
                      fullWidth
                      value={form.new_pin}
                      onChange={e => setForm({ ...form, new_pin: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button fullWidth type="submit" variant="contained" size="large" disabled={saving}>
                      {saving ? 'Guardando...' : 'Guardar cambios'}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card elevation={1} sx={{ p: 1, mt: 3, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Mi horario</Typography>
          <Divider sx={{ mb: 2 }} />
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Día</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Entrada</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Salida</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {DAYS.map(d => (
                  <TableRow key={d.key} hover>
                    <TableCell>
                      <Chip label={d.label} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{formatTime12h(schedulesByDay[d.key]?.entry_time)}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{formatTime12h(schedulesByDay[d.key]?.exit_time)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default WorkerDashboardPage;


